import { createDefaultContactInfo } from "../../interfaces/contactInfo";
import { resolveOrganisationDisplayName } from "../../utils/string";
import { decodeContactInfo, encodeContactInfo } from "../contactInfo";
import { decodeIntegration, encodeIntegration } from "../integration";
export const decodeOrganisation = async (organisation, getImageUrl) => {
    const presignedUrl = organisation.organisation_logo && organisation.organisation_logo.length > 0
        ? await getImageUrl(organisation.organisation_logo)
        : "";
    return {
        abn: organisation.abn,
        contactInfo: organisation.contact_info
            ? decodeContactInfo(organisation.contact_info)
            : createDefaultContactInfo(),
        created: new Date(organisation.created),
        // Resolved once here so every consumer renders the same label, and so the
        // fallback to the legal name lives in one place rather than at 100+ call
        // sites. See OrganisationLineItem.displayName for when NOT to use it.
        displayName: resolveOrganisationDisplayName(organisation.organisation_name, organisation.canonical_name),
        envirotrackEnabled: organisation.envirotrack_enabled,
        govassureEnabled: organisation.govassure_enabled,
        hammertechEnabled: organisation.hammertech_enabled,
        id: organisation.organisation_id,
        integrations: organisation?.integrations?.map((i) => decodeIntegration(i)) || [],
        isContractor: organisation.is_contractor,
        isSetup: organisation.is_setup,
        maxActiveProjects: organisation.max_active_projects,
        mfaEnabled: organisation.mfa_enabled,
        organisationLogo: presignedUrl,
        organisationLogoSrc: organisation.organisation_logo,
        organisationName: organisation.organisation_name,
        planType: organisation.plan_type,
        socialproEnabled: organisation.socialpro_enabled,
        stripeId: organisation.stripe_id,
        subpackageEnabled: organisation.subpackage_enabled,
        transient: organisation.transient,
        users: organisation.users
    };
};
/**
 * Note: `canonical_name` is deliberately not encoded. It is a database-derived
 * generated column — writing it back is at best ignored and at worst an error,
 * and `displayName` is a client-side presentation value with no server field.
 */
export const encodeOrganisation = (organisation) => {
    return {
        abn: organisation.abn,
        contact_info: encodeContactInfo(organisation.contactInfo),
        contact_info_id: organisation.contactInfo.id,
        created: organisation.created,
        envirotrack_enabled: organisation.envirotrackEnabled,
        govassure_enabled: organisation.govassureEnabled,
        hammertech_enabled: organisation.hammertechEnabled,
        integrations: organisation?.integrations?.map((i) => encodeIntegration(i)) || [],
        is_contractor: organisation.isContractor,
        is_setup: organisation.isSetup,
        max_active_projects: organisation.maxActiveProjects,
        mfa_enabled: organisation.mfaEnabled,
        organisation_id: organisation.id,
        organisation_logo: organisation.organisationLogoSrc,
        organisation_name: organisation.organisationName,
        plan_type: organisation.planType,
        socialpro_enabled: organisation.socialproEnabled,
        stripe_id: organisation.stripeId,
        subpackage_enabled: organisation.subpackageEnabled,
        transient: organisation.transient,
        users: organisation.users
    };
};
export const decodeCollaboratorDetail = (collaborator) => {
    return {
        id: collaborator.organisation_id,
        logo: collaborator.organisation_logo,
        name: collaborator.organisation_name
    };
};
export const encodeCollaboratorDetail = (collaborator) => {
    return {
        organisation_id: collaborator.id,
        organisation_logo: collaborator.logo,
        organisation_name: collaborator.name
    };
};
/**
 * Single place that reconciles the named `collaborator_details` with the
 * logo-only `collaborators` it replaces. A backend deployed before
 * `collaborator_details` existed sends only logos, so those still decode into
 * avatars — just without a name for the tooltip.
 */
export const decodeCollaboratorDetails = (collaboratorDetails, collaborators) => {
    if (collaboratorDetails && collaboratorDetails.length > 0) {
        return collaboratorDetails.map(decodeCollaboratorDetail);
    }
    return (collaborators ?? []).map((logo) => ({ id: logo, logo, name: "" }));
};
